import { useRef } from "react";
import { api, apiErr, openFile } from "@/lib/api";
import { toast } from "sonner";
import { ImagePlus, X, ShieldAlert, Paperclip, Clock, Ban } from "lucide-react";

export const emptyProof = () => ({ file: null, waive: false, reason: "" });

// Mandatory payment proof: a screenshot/PDF of the payment. Admins may skip it with a reason;
// staff without proof send the payment for admin approval (it won't count until approved).
export function PaymentProofPicker({ value, onChange, isAdmin, testPrefix = "payment" }) {
  const ref = useRef();
  const set = (patch) => onChange({ ...value, ...patch });
  return (
    <div className="space-y-1.5" data-testid={`${testPrefix}-proof`}>
      <input ref={ref} type="file" accept="image/*,.pdf" className="hidden" data-testid={`${testPrefix}-proof-input`}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) set({ file: f, waive: false }); e.target.value = ""; }} />
      {value.file ? (
        <div className="flex items-center gap-2 text-xs bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg px-2.5 py-1.5" data-testid={`${testPrefix}-proof-chosen`}>
          <Paperclip className="h-3.5 w-3.5 shrink-0" /><span className="truncate flex-1">{value.file.name}</span>
          <button type="button" onClick={() => set({ file: null })} className="text-emerald-700 hover:text-rose-600" title="Remove"><X className="h-3.5 w-3.5" /></button>
        </div>
      ) : (
        <button type="button" onClick={() => ref.current?.click()} data-testid={`${testPrefix}-proof-btn`}
          className="w-full inline-flex items-center justify-center gap-1.5 px-2.5 py-2 rounded-lg border border-dashed border-slate-300 text-xs font-semibold text-slate-600 hover:border-primary hover:text-primary bg-white">
          <ImagePlus className="h-3.5 w-3.5" /> Attach payment proof (screenshot or PDF) *
        </button>
      )}
      {!value.file && (isAdmin ? (
        <div className="space-y-1">
          <label className="flex items-center gap-1.5 text-[11px] text-slate-600">
            <input type="checkbox" data-testid={`${testPrefix}-proof-waive`} checked={value.waive} onChange={(e) => set({ waive: e.target.checked })} /> Record without proof (admin)
          </label>
          {value.waive && <input data-testid={`${testPrefix}-proof-reason`} value={value.reason} onChange={(e) => set({ reason: e.target.value })}
            placeholder="Reason, e.g. cash received in office" className="w-full px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs" />}
        </div>
      ) : (
        <div className="text-[11px] text-amber-700 flex items-start gap-1" data-testid={`${testPrefix}-proof-pending-hint`}>
          <ShieldAlert className="h-3.5 w-3.5 shrink-0 mt-px" /> No proof? The payment goes to an admin for approval and won't count toward the balance until approved.
        </div>
      ))}
    </div>
  );
}

export function proofProblem(proof, isAdmin) {
  if (proof.file) return null;
  if (isAdmin && (!proof.waive || proof.reason.trim().length < 3)) return "Attach a payment screenshot, or tick \"Record without proof\" and give a reason";
  return null;
}

// Uploads the proof into the client's Files (Payment proofs) and records the payment against the invoice.
export async function submitPayment({ invoiceId, clientId, body, proof }) {
  let proofId = null;
  if (proof.file) {
    const fd = new FormData();
    fd.append("file", proof.file);
    fd.append("category", "Payment proofs");
    proofId = (await api.post(`/clients/${clientId}/files`, fd)).data.id;
  }
  try {
    const { data } = await api.post(`/invoices/${invoiceId}/pay`, {
      ...body, ...(proofId ? { proof_file_id: proofId } : {}), ...(!proofId && proof.waive ? { waive_proof_reason: proof.reason.trim() } : {}),
    });
    if (data.pending_approval) toast.message("Sent for admin approval", { description: "It will count toward the balance once an admin approves it — or attach proof later." });
    return data;
  } catch (e) {
    if (proofId) api.delete(`/client-files/${proofId}`).catch(() => {});
    throw e;
  }
}

export function PaymentStateTag({ payment }) {
  const st = payment.status || "Completed";
  if (st === "Pending approval") return <span data-testid={`payment-pending-${payment.id}`} className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full"><Clock className="h-3 w-3" /> Pending approval</span>;
  if (st === "Rejected") return <span data-testid={`payment-rejected-${payment.id}`} title={payment.reject_reason || ""} className="inline-flex items-center gap-1 text-[10px] font-bold text-rose-700 bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded-full"><Ban className="h-3 w-3" /> Rejected</span>;
  if (payment.proof_file_id) return <ProofLink fileId={payment.proof_file_id} testid={`payment-proof-link-${payment.id}`} />;
  if (payment.proof_waived) return <span data-testid={`payment-waived-${payment.id}`} title={`${payment.proof_waived.reason} — ${payment.proof_waived.by_name}`} className="text-[10px] font-semibold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded-full">No proof · approved</span>;
  return null;
}

export function ProofLink({ fileId, testid, label = "Proof" }) {
  const open = async (e) => { e.preventDefault(); e.stopPropagation(); try { await openFile(`/client-files/${fileId}/download`); } catch (err) { toast.error(apiErr(err)); } };
  return <button type="button" onClick={open} data-testid={testid} className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 hover:underline"><Paperclip className="h-3 w-3" /> {label}</button>;
}
